import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trophy, Medal, Award, MapPin, Target, TrendingUp } from "lucide-react";
import type { CanvassingSession, CanvassingPin } from "@/hooks/useCanvassing";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";

interface CanvassingLeaderboardProps {
  sessions: CanvassingSession[];
  pins: CanvassingPin[];
}

interface RepStats {
  userId: string;
  name: string;
  doorsKnocked: number;
  appointmentsSet: number;
  sold: number;
  conversionRate: number;
  sessionsCount: number;
}

export function CanvassingLeaderboard({ sessions, pins }: CanvassingLeaderboardProps) {
  const { orgId } = useAuth();

  // Fetch profiles for names
  const { data: profiles } = useQuery({
    queryKey: ["profiles", orgId],
    queryFn: async () => {
      if (!orgId) return [];
      const { data } = await supabase.from("profiles").select("id, full_name").eq("org_id", orgId);
      return data || [];
    },
    enabled: !!orgId,
  });

  const leaderboard = useMemo(() => {
    const repMap = new Map<string, RepStats>();

    // Aggregate from pins
    pins.forEach(pin => {
      const existing = repMap.get(pin.user_id) || {
        userId: pin.user_id,
        name: "",
        doorsKnocked: 0,
        appointmentsSet: 0,
        sold: 0,
        conversionRate: 0,
        sessionsCount: 0,
      };
      if (pin.outcome !== "not_knocked") existing.doorsKnocked++;
      if (pin.outcome === "appointment_set") existing.appointmentsSet++;
      if (pin.outcome === "sold") existing.sold++;
      repMap.set(pin.user_id, existing);
    });

    // Add session counts
    sessions.forEach(s => {
      const existing = repMap.get(s.user_id);
      if (existing) existing.sessionsCount++;
    });

    // Map names
    const reps = Array.from(repMap.values()).map(r => {
      const profile = profiles?.find(p => p.id === r.userId);
      r.name = profile?.full_name || "Unknown Rep";
      r.conversionRate = r.doorsKnocked > 0 ? Math.round((r.appointmentsSet / r.doorsKnocked) * 100) : 0;
      return r;
    });

    return reps.sort((a, b) => b.appointmentsSet - a.appointmentsSet);
  }, [pins, sessions, profiles]);

  const getRankIcon = (index: number) => {
    if (index === 0) return <Trophy className="w-5 h-5 text-yellow-500" />;
    if (index === 1) return <Medal className="w-5 h-5 text-gray-400" />;
    if (index === 2) return <Award className="w-5 h-5 text-amber-600" />;
    return <span className="w-5 h-5 flex items-center justify-center text-sm font-bold text-muted-foreground">{index + 1}</span>;
  };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
        <Trophy className="w-5 h-5 text-yellow-500" /> Canvassing Leaderboard
      </h2>

      {leaderboard.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Target className="w-10 h-10 mx-auto mb-3 text-muted-foreground/50" />
            <p className="text-muted-foreground">No canvassing data yet</p>
            <p className="text-sm text-muted-foreground/70 mt-1">Start a session and knock some doors!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {leaderboard.map((rep, i) => (
            <Card key={rep.userId} className={i === 0 ? "border-yellow-500/30 bg-yellow-500/5" : ""}>
              <CardContent className="py-3 px-4">
                <div className="flex items-center gap-3">
                  {getRankIcon(i)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">{rep.name}</p>
                    <p className="text-xs text-muted-foreground">{rep.sessionsCount} sessions</p>
                  </div>
                  <div className="flex items-center gap-4 text-center">
                    <div>
                      <p className="text-sm font-bold text-foreground">{rep.doorsKnocked}</p>
                      <p className="text-xs text-muted-foreground">Doors</p>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-green-600 dark:text-green-400">{rep.appointmentsSet}</p>
                      <p className="text-xs text-muted-foreground">Appts</p>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">{rep.sold}</p>
                      <p className="text-xs text-muted-foreground">Sold</p>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      <TrendingUp className="w-3 h-3 mr-1" />
                      {rep.conversionRate}%
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
